import type { SaveValuesResult, Values } from "@gsignatures/core";
import { TIMING } from "../config";
import { ApiFailure, isTransient } from "../api/types";

export type SaveStatus = "idle" | "pending" | "saving" | "saved" | "retrying" | "failed";

export interface SaverEvents {
  status: (s: SaveStatus) => void;
  saved?: (r: SaveValuesResult) => void;
  /** A failure retrying can't fix (the session ended, the link died…). The page decides what to do. */
  fatal?: (e: ApiFailure) => void;
}

/**
 * Autosave for a form's answers: about 600 ms after typing stops, at once on blur.
 * One request at a time; the newest answers always win; a failed save keeps the answers
 * and retries with back-off, so nothing typed is lost while the network comes and goes.
 */
export class Saver {
  private timer: ReturnType<typeof setTimeout> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private waiting: Values | null = null;
  private inflight: Promise<boolean> | null = null;
  private attempt = 0;
  private lastOk = true;
  private disposed = false;
  status: SaveStatus = "idle";

  constructor(
    private readonly save: (v: Values) => Promise<SaveValuesResult>,
    private readonly on: SaverEvents,
    private readonly debounceMs: number = TIMING.saveDebounceMs,
  ) {}

  private set(s: SaveStatus) {
    this.status = s;
    if (!this.disposed) this.on.status(s);
  }

  /** True while answers on this page aren't yet on the server. */
  get dirty(): boolean {
    return this.waiting !== null || this.inflight !== null;
  }

  schedule(values: Values): void {
    this.waiting = values;
    clearTimeout(this.timer);
    if (this.status !== "retrying" && this.status !== "failed") this.set("pending");
    this.timer = setTimeout(() => void this.run(), this.debounceMs);
  }

  /** Saves now (and waits for it). Resolves true when the server has the newest answers. */
  flush(values?: Values): Promise<boolean> {
    if (values) this.waiting = values;
    clearTimeout(this.timer);
    clearTimeout(this.retryTimer);
    return this.run();
  }

  private async run(): Promise<boolean> {
    if (this.disposed) return false;
    if (this.inflight) {
      await this.inflight;
      if (!this.waiting) return this.lastOk;
    }
    const values = this.waiting;
    if (!values) return this.lastOk;
    this.waiting = null;
    if (this.status !== "retrying") this.set("saving");
    const p = this.save(values).then(
      (r) => {
        this.attempt = 0;
        this.lastOk = true;
        if (!this.waiting) this.set("saved");
        this.on.saved?.(r);
        return true;
      },
      (e: unknown) => {
        this.lastOk = false;
        this.waiting ??= values;
        if (isTransient(e)) {
          this.attempt += 1;
          const wait = Math.min(TIMING.saveRetryMs * 2 ** (this.attempt - 1), TIMING.saveRetryMaxMs);
          this.set("retrying");
          clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => void this.run(), wait);
        } else {
          this.set("failed");
          if (e instanceof ApiFailure) this.on.fatal?.(e);
        }
        return false;
      },
    );
    this.inflight = p;
    const ok = await p;
    this.inflight = null;
    if (ok && this.waiting) return this.run();
    return ok;
  }

  dispose(): void {
    this.disposed = true;
    clearTimeout(this.timer);
    clearTimeout(this.retryTimer);
  }
}
